import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import Content from '~/components/layout/Content';
import { PractitionerNameWithCategory } from '~/components/practitioners/PractitionerNameWithCategory';
import DeeplinkTargetFields, { DeeplinkTargetType, getDeeplinkTargetDisplay } from './DeeplinkTargetFields';

const DeeplinkDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [slug, setSlug] = useState('');
    const [targetType, setTargetType] = useState<DeeplinkTargetType>('ITEM');
    const [bookableItemId, setBookableItemId] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [practitionerId, setPractitionerId] = useState<number | null>(null);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const utils = trpc.useUtils();

    const { data: deeplinkData, isLoading } = trpc.bookingFunnelDeeplinks.getOne.useQuery(
        { id: id! },
        { enabled: !!id }
    );

    const { data: bookableData } = trpc.bookingFunnelDeeplinks.getBookableItemsForSelect.useQuery();
    const { data: categoriesData } = trpc.bookingFunnelDeeplinks.getCategoriesForSelect.useQuery();
    const { data: practitionersData } = trpc.bookingFunnelDeeplinks.getPractitionersForSelect.useQuery();

    const updateMutation = trpc.bookingFunnelDeeplinks.update.useMutation({
        onSuccess: () => {
            toast.success('Deeplink gespeichert');
            utils.bookingFunnelDeeplinks.getAll.invalidate();
            utils.bookingFunnelDeeplinks.getOne.invalidate({ id: id! });
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const deleteMutation = trpc.bookingFunnelDeeplinks.delete.useMutation({
        onSuccess: () => {
            toast.success('Deeplink gelöscht');
            utils.bookingFunnelDeeplinks.getAll.invalidate();
            navigate('/cms/deeplinks');
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const row = deeplinkData?.data;

    useEffect(() => {
        if (row) {
            setSlug(row.slug);
            setTargetType(row.categoryId ? 'CATEGORY' : 'ITEM');
            setBookableItemId(row.bookableItemId ?? '');
            setCategoryId(row.categoryId ?? '');
            setPractitionerId(row.practitionerId ?? null);
        }
    }, [row]);

    if (!id) {
        return null;
    }

    if (isLoading) {
        return (
            <Content>
                <div className='container mx-auto py-10'>
                    <Skeleton className='h-10 w-64 mb-6' />
                    <Skeleton className='h-64 w-full' />
                </div>
            </Content>
        );
    }

    if (!row) {
        return (
            <Content>
                <div className='container mx-auto py-10'>
                    <div className='text-center py-12 text-muted-foreground'>Deeplink nicht gefunden.</div>
                </div>
            </Content>
        );
    }

    const bookableItems = bookableData?.data ?? [];
    const categories = categoriesData?.data ?? [];
    const practitioners = practitionersData?.data ?? [];
    const target = getDeeplinkTargetDisplay(row);
    const nextBookableItemId = targetType === 'ITEM' ? bookableItemId : '';
    const nextCategoryId = targetType === 'CATEGORY' ? categoryId : '';
    const dirty =
        slug.trim() !== row.slug ||
        nextBookableItemId !== (row.bookableItemId ?? '') ||
        nextCategoryId !== (row.categoryId ?? '') ||
        practitionerId !== (row.practitionerId ?? null);

    const handleSave = () => {
        if (!slug.trim()) {
            toast.error('Bitte geben Sie einen Slug ein');
            return;
        }
        if (targetType === 'ITEM' && !bookableItemId) {
            toast.error('Bitte wählen Sie eine buchbare Leistung');
            return;
        }
        if (targetType === 'CATEGORY' && !categoryId) {
            toast.error('Bitte wählen Sie eine Kategorie');
            return;
        }
        updateMutation.mutate({
            id: row.id,
            slug: slug.trim(),
            bookableItemId: nextBookableItemId || null,
            categoryId: nextCategoryId || null,
            practitionerId,
        });
    };

    return (
        <Content>
            <div className='container mx-auto py-10'>
                <div className='flex items-center gap-4 mb-6'>
                    <Button variant='ghost' size='icon' onClick={() => navigate('/cms/deeplinks')}>
                        <ArrowLeft className='h-4 w-4' />
                    </Button>
                    <div className='flex-1'>
                        <div className='flex items-center gap-3 flex-wrap'>
                            <h1 className='text-2xl font-bold font-mono sm:text-3xl break-all'>{row.slug}</h1>
                            <Badge variant='secondary' className='text-xs'>
                                {target.typeLabel}
                            </Badge>
                        </div>
                        <p className='text-muted-foreground mt-1'>
                            {target.nameDe} · {target.nameEn}
                        </p>
                    </div>
                    <Button variant='destructive' size='sm' onClick={() => setDeleteOpen(true)}>
                        <Trash2 className='h-4 w-4 mr-2' />
                        Löschen
                    </Button>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>Einstellungen</CardTitle>
                        <CardDescription>Slug und Ziel (buchbare Leistung oder Kategorie) anpassen.</CardDescription>
                    </CardHeader>
                    <CardContent className='space-y-4 max-w-xl'>
                        <div className='space-y-2'>
                            <Label>Slug</Label>
                            <Input value={slug} onChange={(e) => setSlug(e.target.value)} />
                            <p className='text-xs text-muted-foreground'>
                                Nur Kleinbuchstaben, Ziffern und Bindestriche (keine führenden/abschließenden Bindestriche).
                            </p>
                        </div>
                        <DeeplinkTargetFields
                            targetType={targetType}
                            onTargetTypeChange={setTargetType}
                            bookableItems={bookableItems}
                            bookableItemId={bookableItemId}
                            onBookableItemIdChange={setBookableItemId}
                            categories={categories}
                            categoryId={categoryId}
                            onCategoryIdChange={setCategoryId}
                        />
                        <div className='space-y-2'>
                            <Label>Behandler (optional)</Label>
                            <Select
                                value={practitionerId != null ? String(practitionerId) : 'none'}
                                onValueChange={(v) => setPractitionerId(v === 'none' ? null : Number(v))}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder='Kein Behandler' />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value='none'>Kein Behandler</SelectItem>
                                    {practitioners.map((p) => (
                                        <SelectItem key={p.id} value={String(p.id)}>
                                            <PractitionerNameWithCategory
                                                firstName={p.firstName}
                                                lastName={p.lastName}
                                                title={p.title}
                                                categoryName={p.practitioner_category?.name}
                                            />
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className='text-xs text-muted-foreground'>
                                Wenn ein Behandler ausgewählt ist, werden im Buchungstool nur Termine dieses Behandlers angezeigt.
                            </p>
                        </div>
                        <Button onClick={handleSave} disabled={!dirty || updateMutation.isPending}>
                            {updateMutation.isPending ? 'Speichern…' : 'Speichern'}
                        </Button>
                    </CardContent>
                </Card>

                <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Deeplink löschen?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Der Deeplink &quot;{row.slug}&quot; wird deaktiviert und erscheint nicht mehr in der Übersicht.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                            <AlertDialogAction
                                className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                                onClick={() => deleteMutation.mutate({ id: row.id })}
                            >
                                Löschen
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </Content>
    );
};

export default DeeplinkDetail;
